import { memo, useState, useCallback, useEffect } from 'react'
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Badge } from "@/shared/components/ui/badge"
import { Switch } from "@/shared/components/ui/switch"
import { Checkbox } from "@/shared/components/ui/checkbox"
import { Card } from "@/shared/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/shared/components/ui/alert-dialog"
import { X, Plus, Search, RefreshCw, Loader2 } from "lucide-react"
import { SkillApiService } from "@/modules/agent/services/skill-api"
import type { SkillInfo, ProjectSkillItem } from "@/modules/agent/types/skill"
import { useToast } from "@/shared/hooks/useToast"

// ==================== 项目技能配置面板 ====================
// 飞书文档第九节：技能发布与撤回功能
// - 发布 = 从技能库选择技能添加到项目 manifest，agent 运行时注入该技能提示词
// - 撤回 = 从 manifest 移除，agent 不再感知
// - 启用/禁用 = 临时切换 enabled 状态，不从 manifest 删除
interface ProjectSkillsPanelProps {
  projectId: string
  onClose: () => void
}

export const ProjectSkillsPanel = memo(({ projectId, onClose }: ProjectSkillsPanelProps) => {
  // ====== 数据状态 ======
  const [allSkills, setAllSkills] = useState<SkillInfo[]>([])
  const [projectSkills, setProjectSkills] = useState<ProjectSkillItem[]>([])
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false)
  const [isLoadingProject, setIsLoadingProject] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const { showError, showSuccess } = useToast()

  // ====== 发布技能 Dialog 状态 ======
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false)
  const [publishSelection, setPublishSelection] = useState<Set<string>>(new Set())
  const [publishSearchQuery, setPublishSearchQuery] = useState('')

  // ====== 撤回确认 AlertDialog 状态 ======
  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false)
  const [skillToWithdraw, setSkillToWithdraw] = useState<string | null>(null)

  // ====== 数据加载 ======
  const loadLibrary = useCallback(async () => {
    setIsLoadingLibrary(true)
    try {
      const resp = await SkillApiService.getSkillLibrary()
      if (resp.success) setAllSkills(resp.data.skills || [])
    } catch { showError('加载技能库失败') }
    finally { setIsLoadingLibrary(false) }
  }, [showError])

  const loadProjectSkills = useCallback(async () => {
    if (!projectId) return
    setIsLoadingProject(true)
    try {
      const resp = await SkillApiService.getProjectSkills(projectId)
      if (resp.success) setProjectSkills(resp.data.skills || [])
    } catch { /* 404 = 项目还没配过技能，使用空列表 */ }
    finally { setIsLoadingProject(false) }
  }, [projectId])

  useEffect(() => { loadLibrary(); loadProjectSkills() }, [loadLibrary, loadProjectSkills])

  // ====== 发布技能逻辑 ======
  const availableSkills = allSkills.filter(skill => 
    !projectSkills.some(ps => ps.name === skill.name) &&
    skill.name.toLowerCase().includes(publishSearchQuery.toLowerCase())
  )

  const handlePublish = useCallback(async () => {
    if (publishSelection.size === 0) return
    setIsSaving(true)
    try {
      const skillsToPublish = Array.from(publishSelection).map(skillName => ({ name: skillName, enabled: true }))
      const newSkills = [...projectSkills, ...skillsToPublish]
      const resp = await SkillApiService.updateProjectSkills(projectId, { skills: newSkills })
      if (resp.success) {
        showSuccess(`成功发布 ${publishSelection.size} 个技能`)
        setPublishSelection(new Set())
        setIsPublishDialogOpen(false)
        loadProjectSkills()
      } else {
        showError('发布失败')
      }
    } catch { showError('发布技能失败') }
    finally { setIsSaving(false) }
  }, [projectId, publishSelection, projectSkills, showSuccess, showError, loadProjectSkills])

  // ====== 撤回技能逻辑 ======
  const handleWithdraw = useCallback(async () => {
    if (!skillToWithdraw) return
    setIsSaving(true)
    try {
      const updatedSkills = projectSkills.filter(skill => skill.name !== skillToWithdraw)
      const resp = await SkillApiService.updateProjectSkills(projectId, { skills: updatedSkills })
      if (resp.success) {
        showSuccess('技能撤回成功')
        setSkillToWithdraw(null)
        setIsWithdrawDialogOpen(false)
        loadProjectSkills()
      } else {
        showError('撤回失败')
      }
    } catch { showError('撤回技能失败') }
    finally { setIsSaving(false) }
  }, [projectId, skillToWithdraw, projectSkills, showSuccess, showError, loadProjectSkills])

  // ====== 启用/禁用技能逻辑 ======
  const handleToggleEnabled = useCallback(async (skillName: string, enabled: boolean) => {
    setIsSaving(true)
    try {
      const updatedSkills = projectSkills.map(skill => 
        skill.name === skillName ? { ...skill, enabled } : skill
      )
      const resp = await SkillApiService.updateProjectSkills(projectId, { skills: updatedSkills })
      if (resp.success) {
        showSuccess(enabled ? '技能已启用' : '技能已禁用')
        loadProjectSkills()
      } else {
        showError('操作失败')
      }
    } catch { showError('操作失败') }
    finally { setIsSaving(false) }
  }, [projectId, projectSkills, showSuccess, showError, loadProjectSkills])

  return (
    <div className="w-80 bg-white border-l border-slate-200 shadow-lg flex flex-col">
      {/* 面板头部 */}
      <div className="p-4 border-b flex justify-between items-center bg-slate-50">
        <h3 className="font-medium text-sm">项目技能配置</h3>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsPublishDialogOpen(true)}
            className="h-7 text-xs gap-1"
          >
            <Plus className="h-3 w-3" />
            发布技能
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* 面板内容 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* 刷新按钮 */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-600">已发布技能</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { loadLibrary(); loadProjectSkills() }}
            disabled={isLoadingLibrary || isLoadingProject}
            className="h-6 w-6 p-0"
          >
            <RefreshCw className={`h-3 w-3 ${(isLoadingLibrary || isLoadingProject) ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* 加载状态 */}
        {isLoadingProject ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : projectSkills.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <div className="text-2xl mb-2">📚</div>
            <p className="text-sm">暂无已发布的技能</p>
            <p className="text-xs text-slate-400 mt-1">点击"发布技能"添加</p>
          </div>
        ) : (
          <div className="space-y-3">
            {projectSkills.map((projectSkill) => {
              const skill = allSkills.find(s => s.name === projectSkill.name)
              return (
                <Card key={projectSkill.name} className="p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-sm truncate">
                          {skill?.name || projectSkill.name}
                        </h4>
                        <Badge variant={projectSkill.enabled ? "default" : "secondary"} className="text-xs">
                          {projectSkill.enabled ? "启用" : "禁用"}
                        </Badge>
                      </div>
                      {skill?.description && (
                        <p className="text-xs text-slate-500 line-clamp-2">
                          {skill.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`skill-${projectSkill.name}`} className="text-xs">
                            启用状态
                          </Label>
                          <Switch
                            id={`skill-${projectSkill.name}`}
                            checked={projectSkill.enabled}
                            onCheckedChange={(enabled) => handleToggleEnabled(projectSkill.name, enabled)}
                            disabled={isSaving}
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSkillToWithdraw(skill?.name || projectSkill.name)
                            setIsWithdrawDialogOpen(true)
                          }}
                          disabled={isSaving}
                          className="h-6 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          撤回
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      {/* ====== 发布技能 Dialog ====== */}
      <Dialog open={isPublishDialogOpen} onOpenChange={setIsPublishDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>发布技能到项目</DialogTitle>
            <DialogDescription>
              从技能库中选择技能发布到当前项目，Agent 将能够使用这些技能。
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col">
            {/* 搜索框 */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="搜索技能..."
                value={publishSearchQuery}
                onChange={(e) => setPublishSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* 技能列表 */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {isLoadingLibrary ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : availableSkills.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <div className="text-2xl mb-2">🔍</div>
                  <p className="text-sm">
                    {publishSearchQuery ? '未找到匹配的技能' : '所有技能都已发布'}
                  </p>
                </div>
              ) : (
                availableSkills.map((skill) => (
                  <Card 
                    key={skill.name} 
                    className={`p-3 cursor-pointer transition-colors ${
                      publishSelection.has(skill.name) ? 'bg-blue-50 border-blue-200' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => {
                      const newSelection = new Set(publishSelection)
                      if (newSelection.has(skill.name)) {
                        newSelection.delete(skill.name)
                      } else {
                        newSelection.add(skill.name)
                      }
                      setPublishSelection(newSelection)
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={publishSelection.has(skill.name)}
                        onChange={() => {}} // 由外层 onClick 处理
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm">{skill.name}</h4>
                        {skill.description && (
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                            {skill.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {skill.type || '通用'}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {skill.tags?.join(', ') || '无标签'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPublishDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handlePublish} disabled={publishSelection.size === 0}>
              确认发布{publishSelection.size > 0 && ` (${publishSelection.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== 撤回确认 AlertDialog ====== */}
      <AlertDialog open={isWithdrawDialogOpen} onOpenChange={setIsWithdrawDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认撤回</AlertDialogTitle>
            <AlertDialogDescription>
              确认撤回技能 <strong>{skillToWithdraw}</strong>？撤回后该项目的 Agent 将不再使用此技能。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSkillToWithdraw(null)}>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleWithdraw} className="bg-red-500 hover:bg-red-600">
              撤回
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
})

ProjectSkillsPanel.displayName = 'ProjectSkillsPanel'